import React from 'react';
import { useDispatch } from 'react-redux';
import type { Claim } from '../../api/claims/interface';
import { StoreState, useSelector } from '../../data/store';
import { actions } from './data/logic';

interface ClaimOverviewHookState {
  claimMetadata?: Claim;
  isDrawerOpen: boolean;
  actions: StoreState['claim-overview']['actions'];
}

interface ClaimOverviewHookHandler {
  fetchClaim(): void;
  generateSummariesAndInsights(): void;
  setDrawerOpen: (isOpen: boolean) => void;
  setLastViewed: () => void;
}

const useClaimOverviewHook = (
  dossierId: string
): [ClaimOverviewHookState, ClaimOverviewHookHandler] => {
  const {
    claimMetadata,
    isDrawerOpen,
    actions: claimOverviewActions,
  } = useSelector((s: StoreState) => s['claim-overview']);
  const dispatch = useDispatch();

  const handler = React.useMemo(
    () => ({
      fetchClaim: () => {
        dispatch(actions.fetchClaim({ dossierId }));
      },
      generateSummariesAndInsights: () => {
        dispatch(actions.generateSummariesAndInsights({ dossierId }));
      },
      setDrawerOpen: (isOpen: boolean) => {
        dispatch(actions.setDrawerOpen(isOpen));
      },
      setGeneratingSummariesAndInsights: (generating: boolean) => {
        dispatch(
          actions.setGeneratingSummariesAndInsights({ dossierId, generating })
        );
      },
      setLastViewed: () => {
        dispatch(actions.setLastViewed({ dossierId, timestamp: Date.now() }));
      },
    }),
    [dossierId, dispatch]
  );

  return [
    {
      claimMetadata: claimMetadata[dossierId],
      isDrawerOpen,
      actions: claimOverviewActions,
    },
    handler,
  ];
};

export default useClaimOverviewHook;
